import {ChangeDetectionStrategy, Component, inject, Input} from '@angular/core';
import {MarketSeriesManager} from '../market-series-manager';
import {
  EventBank,
  MonthColumn,
  MonthRateRow,
  PolicyEvent,
  pointsLabel,
  YearRow,
} from '../policy-chart.models';

@Component({
  selector: 'app-fiscal-rate-table',
  standalone: true,
  imports: [],
  templateUrl: './fiscal-rate-table.html',
  styleUrl: './fiscal-rate-table.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FiscalRateTable {
  private readonly seriesManager = inject(MarketSeriesManager);

  @Input({required: true}) year!: YearRow;

  /** The banks the manager is showing, the Fed first. */
  get rateRows(): MonthRateRow[] {
    return this.year.rateTable.filter((row) => this.seriesManager.isBankVisible(row.bank));
  }

  bankColor(bank: EventBank): string {
    return this.seriesManager.bankColor(bank);
  }

  bankName(bank: EventBank): string {
    return this.seriesManager.bankName(bank);
  }

  get showRbaIndicators(): boolean {
    return this.seriesManager.isBankVisible('RBA') && this.year.rbaIndicators !== undefined;
  }

  get showBcbIndicators(): boolean {
    return this.seriesManager.isBankVisible('BCB') && this.year.bcbIndicators !== undefined;
  }

  /**
   * The bank's colour washed down to a pastel, so a row is recognisable by its
   * background as well as by its name. Translucent rather than a mixed-with-white
   * hex, so a hovered or emergency column still shows through underneath.
   */
  bankTint(bank: EventBank): string {
    const hex = this.seriesManager.bankColor(bank).replace('#', '');
    const full = hex.length === 3 ? [...hex].map((part) => part + part).join('') : hex;
    const [red, green, blue] = [0, 2, 4].map((start) =>
      parseInt(full.slice(start, start + 2), 16));
    return `rgba(${red}, ${green}, ${blue}, .1)`;
  }

  /** Every bank's rate added together for one month, the Fed at its range midpoint. */
  monthTotal(monthIndex: number): string | null {
    const values = this.rateRows
      .map((row) => row.values[monthIndex])
      .filter((value): value is number => value !== null);
    if (!values.length) return null;
    return `${Number(values.reduce((total, value) => total + value, 0).toFixed(2))}%`;
  }

  /** Net of every visible bank's move that month. */
  monthNet(column: MonthColumn): {points: number; label: string} | null {
    return this.sumMoves(column.events.filter((event) =>
      event.changePoints !== undefined && this.seriesManager.isBankVisible(event.bank)));
  }

  /** Whether any bank on screen decided outside its calendar that month. */
  hasEmergency(column: MonthColumn): boolean {
    return this.emergencies(column).length > 0;
  }

  /** How many such decisions, for the total row. */
  emergencyCount(column: MonthColumn): number {
    return this.emergencies(column).length;
  }

  /** When one bank moved its rate that month. */
  changeDates(bank: EventBank, column: MonthColumn): {label: string; title: string} {
    return this.dates(column.events.filter((event) =>
      event.bank === bank && event.changePoints !== undefined));
  }

  /** When one bank decided outside its calendar that month. */
  emergencyDates(bank: EventBank, column: MonthColumn): {label: string; title: string} {
    return this.dates(this.emergencies(column).filter((event) => event.bank === bank));
  }

  /**
   * 'Mar 17' for a single date. A bank can move more than once in a month —
   * Canada cut three times in March 2020 — and the column is too narrow to list
   * them, so the rest are counted and spelled out in the cell's tooltip.
   */
  private dates(events: PolicyEvent[]): {label: string; title: string} {
    const labels = events.map((event) => event.dateLabel);
    if (!labels.length) return {label: '', title: ''};
    return {
      label: labels.length > 1 ? `${labels[0]}+${labels.length - 1}` : labels[0],
      title: labels.join(', '),
    };
  }

  private emergencies(column: MonthColumn): PolicyEvent[] {
    return column.events.filter((event) =>
      event.emergency && this.seriesManager.showsEvent(event.bank));
  }

  /**
   * What one bank did to its rate in one month. A bank can move more than once
   * in a month — Canada cut three times in March 2020 — so these are summed.
   */
  monthChange(bank: EventBank, column: MonthColumn): {points: number; label: string} | null {
    return this.sumMoves(column.events.filter(
      (event) => event.bank === bank && event.changePoints !== undefined,
    ));
  }

  bankClass(bank: EventBank): string {
    return bank.toLowerCase();
  }

  private sumMoves(moves: {changePoints?: number}[]): {points: number; label: string} | null {
    if (!moves.length) return null;
    const points = Number(moves.reduce((total, move) => total + move.changePoints!, 0).toFixed(4));
    return {points, label: pointsLabel(points)};
  }
}
